<template>
  <div>
    <v-menu bottom max-width="250" nudge-bottom="10" nudge-left="10" :open-on-click="false" :close-on-click="false" :close-on-content-click="false"
            content-class="tour-arrow tour-arrow-left purple white--text pt-6 px-4" :value="reminder">
      <template v-slot:activator="{on}">
        <v-tooltip bottom open-delay="500">
          <template v-slot:activator="{on:tooltip}">
            <v-btn fab color="green" x-small v-show="show" @click="refresh();showSetupDialog=true" v-on="on" class="setup-button">
              <v-icon color="white" v-on="tooltip">mdi-auto-fix</v-icon>
            </v-btn>
          </template>
          Finish setup
        </v-tooltip>
      </template>
      To resume setup, click here
      <v-btn plain class="white--text mt-4 float-right" @click.stop="$store.commit('setSetupReminder');reminder=false">OK</v-btn>
    </v-menu>

    <v-dialog :value="showSetupDialog" persistent max-width="600">
      <v-card>
        <v-card-title class="text-h6">
          <template v-if="welcome">Welcome to PŪRTRAK!</template>
          <template v-else>
            <v-icon color="green" left>mdi-auto-fix</v-icon>Distillery setup
          </template>
        </v-card-title>

        <v-card-text class="pt-2">
          <div v-if="welcome && video" ref="video">
            <iframe width="560" height="315"
                    :src="'https://www.youtube.com/embed/'+video+'?enablejsapi=1&modestbranding=1'"
                    title="YouTube video player" frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen/>
          </div>

          <template v-else-if="progress===100">
            <div class="text-h6 green--text mb-2">CONGRATULATIONS!</div>
            You've successfully setup the essential elements of PŪRTRAK and are now ready to start using the program.
            There are a few additional things you might want to do:
            <ul class="mt-2">
              <li v-if="settings.distilling||settings.mashing||settings.blending">Define recipes for mashing (i.e. Mash bills), distilling (i.e. Processes), and blending (i.e. Formulas) from the workflow screen</li>
              <li>Make sure your bill of materials are complete by editing your products</li>
              <li>Add your customers and suppliers</li>
              <li>Look at some of the preferences under settings</li>
              <li v-if="company.maxUsers>1">Give access to other users (in settings)</li>
              <li v-if="company.allowAccounting">Connect QuickBooks online (go to Apps)</li>
            </ul>
          </template>

          <template v-else>
            <v-progress-linear :value="progress"  height="25" color="green" class="mb-5">
              <template v-slot:default="{ value }">
                <strong>{{ Math.ceil(value) }}%</strong>
              </template>
            </v-progress-linear>


            <template v-if="section===0">
              <div class="text-body-1">To help you get started, please tell us what activities you do in your distillery</div>
              <v-list dense>
                <v-list-item><v-checkbox label="Mash and/or ferment" v-model="settings.mashing" hide-details
                                         @change="settings.distilling = settings.distilling || settings.mashing;saveWorkflow()"/></v-list-item>
                <v-list-item><v-checkbox label="Distill" v-model="settings.distilling" hide-details @change="saveWorkflow()" :disabled="settings.mashing"/></v-list-item>
                <v-list-item><v-checkbox label="Blend" v-model="settings.blending" hide-details @change="saveWorkflow()"/></v-list-item>
                <v-list-item><v-checkbox label="Mature spirits in barrel" v-model="settings.casks" hide-details @change="saveWorkflow()"/></v-list-item>
                <v-list-item><v-checkbox label="Receive or ship bulk spirits in tote, drum, etc." v-model="settings.bulkContainers" hide-details @change="saveWorkflow()"/></v-list-item>
                <v-list-item v-if="company.allowWine"><v-checkbox label="Distill and/or blend wine with spirits" v-model="settings.wine" hide-details @change="saveWorkflow()"/></v-list-item>
              </v-list>
            </template>

            <template v-else-if="section===1">
              <div class="text-body-1">Some basic information about your distillery should be entered</div>
              <v-list>
                <v-list-item :to="{name:'Settings',params:{initialTab:0}}" @click="closeSetupDialog()">
                  <v-list-item-icon><v-icon :class="info?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':info}">Distillery information</v-list-item-title>
                    <v-list-item-subtitle>Contact information and your business number</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item :to="{name:'Settings',params:{initialTab:4}}" @click="closeSetupDialog()">
                  <v-list-item-icon><v-icon :class="excise?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':excise}">Sites / Excise licenses</v-list-item-title>
                    <v-list-item-subtitle>Excise license numbers for each site you operate</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>

            <template v-else-if="section===2">
              <div class="text-body-1">We need to know about the types of alcohol you work with</div>
              <v-list>
                <v-list-item :to="{name:'Spirits'}" @click="closeSetupDialog()">
                  <v-list-item-icon><v-icon :class="spirits?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':spirits}">Spirits</v-list-item-title>
                    <v-list-item-subtitle>All the spirits used and/or produced in distilling, blending and bottling</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item :to="{name:'Wine'}" @click="closeSetupDialog()" v-if="settings.wine">
                  <v-list-item-icon><v-icon :class="wines?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':wines}">Wine</v-list-item-title>
                    <v-list-item-subtitle>The types of wine you distill or blend</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>

            <template v-else-if="section===3">
              <div class="text-body-1">The equipment you use for production needs to be defined</div>
              <v-list>
                <v-list-item :to="{name:'EquipmentTanks'}" @click="closeSetupDialog()">
                  <v-list-item-icon><v-icon :class="tanks?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':tanks}">Tanks</v-list-item-title>
                    <v-list-item-subtitle>Each tank, along with current contents</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
                <v-list-item :to="{name:'EquipmentStills'}" @click="closeSetupDialog()" v-if="settings.distilling">
                  <v-list-item-icon><v-icon :class="stills?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':stills}">Stills</v-list-item-title>
                    <v-list-item-subtitle>The stills you use for distilling</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              </v-list>
                <v-list-item :to="{name:'EquipmentFermenters'}" @click="closeSetupDialog()" v-if="settings.mashing">
                  <v-list-item-icon><v-icon :class="fermenters?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':fermenters}">Fermenters</v-list-item-title>
                    <v-list-item-subtitle>Fermenters, with any initial contents</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>

            <template v-else-if="section===4">
              <div class="text-body-1">All inventory used and produced</div>
              <v-list>
                <v-list-item :to="{name:'InventoryItems'}" @click="closeSetupDialog()">
                  <v-list-item-icon><v-icon :class="items?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':items}">Items & services</v-list-item-title>
                    <v-list-item-subtitle>All non-alcohol raw materials used in production</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-list>
                <v-list-item :to="{name:'InventoryProducts'}" @click="closeSetupDialog()">
                  <v-list-item-icon><v-icon :class="products?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':products}">Bottled products</v-list-item-title>
                    <v-list-item-subtitle>Products you make and sell and their current inventory levels</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-list>
                <v-list-item :to="{name:'InventoryCasks'}" @click="closeSetupDialog()" v-if="settings.casks">
                  <v-list-item-icon><v-icon :class="casks?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':casks}">Casks</v-list-item-title>
                    <v-list-item-subtitle>Current inventory of aging barrels of spirits</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-list>
                <v-list-item :to="{name:'InventoryBulkContainers'}" @click="closeSetupDialog()" v-if="settings.bulkContainers">
                  <v-list-item-icon><v-icon :class="bulk?'green--text mr-2':'grey--text text--lighten-1 mr-2'">mdi-check-circle</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{'text-decoration-line-through':bulk}">Bulk containers</v-list-item-title>
                    <v-list-item-subtitle>Any totes, drums, pails, etc. containing alcohol</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
          </template>
        </v-card-text>

        <v-card-actions class="pr-6">
          <template v-if="welcome">
            <v-spacer/>
            <v-btn color="primary" plain v-if="showSetup" @click="welcomeDone()">Start setup</v-btn>
            <v-btn color="primary" plain v-else @click="welcomeDone();showSetupDialog=false">Start using PŪRTRAK</v-btn>
          </template>
          <template v-else-if="progress===100">
            <v-spacer/>
            <v-btn color="primary" @click="allDone()">OK</v-btn>
          </template>
          <template v-else>
            <v-btn color="primary" plain @click="closeSetupDialog()">Finish later</v-btn>
            <v-spacer/>
            <v-btn color="primary" @click="section--" :disabled="section===0"><<</v-btn>
            <v-btn color="primary" @click="section++" :disabled="section===4">>></v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.setup-button {
  animation: buttonBrightness 5s linear infinite;
}
@keyframes buttonBrightness {
  0%{filter:brightness(.75)}
  25%{filter:brightness(1)}
  50%{filter:brightness(1.25)}
  75%{filter:brightness(1)}
  100%{filter:brightness(.75)}
}
</style>

<script>
export default {
  name: 'Setup',
  props: {
    welcome: {type:Boolean, required:false}
  },
  data() {
    return {
      show: false,
      showSetup: false,
      showSetupDialog: false,
      reminder: false,
      section: 0,
      video: '',
      settings: {},

      // Options
      options: {
        mash: true,
        distill: true,
        blend: true,
        cask: true,
        bulk: false,
        wine: false,
      },

      info: false,
      excise: false,

      spirits: false,
      wines: false,
      stills: false,
      tanks: false,
      fermenters: false,

      items: false,
      products: false,
      casks: false,
      bulk: false,
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    progress() {
      const limit = 7 +   // Create, info, sites, spirits, tanks, items, products
                    (this.company.allowWine && this.settings.wine ? 1: 0) +       // wine
                    (this.settings.distilling ? 1 : 0) +   // stills
                    (this.settings.mashing ? 1 : 0) +      // fermenters
                    (this.settings.casks ? 1 : 0) +
                    (this.settings.bulkContainers ? 1 : 0);
      const progress = 1 +          // For starting!
                       (this.info ? 1: 0) + (this.excise ? 1 : 0) +
                       (this.spirits ? 1 : 0) + (this.company.allowWine && this.settings.wine && this.wines ? 1: 0) +
                       (this.tanks ? 1 : 0) + (this.settings.distilling && this.stills ? 1 : 0) + (this.settings.mashing && this.fermenters ? 1 : 0) +
                       (this.items ? 1 : 0) + (this.products ? 1 : 0) + (this.settings.casks && this.casks ? 1 : 0) + (this.settings.bulkContainers && this.bulk ? 1 : 0);
      return progress / limit * 100;
    },
  },
  async created() {
    await this.refresh();
    this.show = this.showSetup || this.welcome;
    this.showSetupDialog = this.show;
    if (!this.show)
      this.allDone();
  },
  methods: {
    async refresh() {
      // Check if outstanding setup items and set this.show
      const config = this.permission('config.write');
      const inventory = this.permission('inventory.write');
      const production = this.permission('production.write');
      const results = await this.$apiCall(`query Setup($inventory:Boolean!,$production:Boolean!,$config:Boolean!) {
                                              productionOverviewPreferences {
                                                sales,purchasing,mashing,distilling,blending,wine,casks,bulkContainers
                                              }
                                              company @include(if:$config) {address,phone,businessNumber, sites{spiritsLicense,warehouseLicense}}
                                              accountingIntegrationProvider @include(if:$config) {name}
                                              fermentersActiveCount @include(if:$production)
                                              spiritsActiveCount @include(if:$inventory)
                                              winesActiveCount @include(if:$inventory)
                                              tanksActiveCount @include(if:$production)
                                              stillsActiveCount @include(if:$production)
                                              itemsActiveCount @include(if:$inventory)
                                              productsActiveCount @include(if:$inventory)
                                              casksCount @include(if:$inventory)
                                              bulkContainersCount @include(if:$inventory)
                                              video(key:"setup")
                                            }`,
                                          {inventory, production, config});
      if (!results.error) {
        this.settings = results.data.productionOverviewPreferences;
        this.video = results.data.video;
        this.info = config && !!results.data.company.address && !!results.data.company.phone && !!results.data.company.businessNumber;
        this.excise = config && results.data.company.sites.reduce((okay,site) => {return okay && site.spiritsLicense!='' && site.warehouseLicense!=''}, true);

        this.spirits = inventory && results.data.spiritsActiveCount !== 0;
        this.wines = inventory && results.data.winesActiveCount !== 0;

        this.stills = production && results.data.stillsActiveCount !== 0;
        this.tanks = production && results.data.tanksActiveCount !== 0;
        this.fermenters = production && results.data.fermentersActiveCount !== 0;

        this.items = inventory && results.data.itemsActiveCount !== 0;
        this.products = inventory && results.data.productsActiveCount !== 0;
        this.casks = inventory && results.data.casksCount !== 0;
        this.bulk = inventory && results.data.bulkContainersCount !== 0;
      }

      this.settings.mashing = this.settings.mashing ?? true;
      this.settings.distilling = this.settings.distilling ?? true;
      this.settings.blending = this.settings.blending ?? true;
      this.settings.wine = this.settings.wine ?? false;
      this.settings.casks = this.settings.casks ?? true;
      this.settings.bulkContainers = this.settings.bulkContainers ?? false;

      this.section = 0;
      if (config && (!this.info || !this.excise))
        this.section = 1;
      else if (inventory && (!this.spirits || (this.company.allowWine && this.settings.wine && !this.wines)))
        this.section = 2;
      else if (production && ((this.settings.distilling && !this.stills) || !this.tanks || (this.settings.mashing && !this.fermenters)))
        this.section = 3;
      else if (inventory && (!this.items || !this.products || (this.settings.casks && !this.casks) || (this.settings.bulkContainers && !this.bulk)))
        this.section = 4;
      if (this.section)
        this.showSetup = true;
      if (this.welcome)
        this.section = 0;
    },
    permission(access) {
      return this.company.permissions.includes(access);
    },
    welcomeDone() {
      const iframe = this.$refs.video.getElementsByTagName("iframe")[0].contentWindow;
      iframe.postMessage(JSON.stringify({ event: "command", func: "stopVideo", args:"" }), '*');

      delete this.$store.getters.userSettings.welcome;
      this.$store.commit('setUserSettings', this.$store.getters.userSettings);
    },
    closeSetupDialog() {
      this.showSetupDialog = false;
      if (!localStorage.getItem('setupReminder')) {
        localStorage.setItem('setupReminder', '1');
        this.reminder = 1;
      }
    },
    async allDone() {
      this.showSetupDialog = false;
      const input = this.$store.getters.company.settings;
      input.doneSetup = true;
      await this.$apiCall(`mutation Setup($input:CompanySettingsInput!) {
                            companySettingsSet(input:$input) {
                              id
                            }
                          }`,
                          {input});
    },
    async saveWorkflow() {
      await this.$apiCall(`mutation Setup($input:ProductionOverviewPreferencesInput!) {
                            productionOverviewSetPreferences(input:$input) {
                              casks
                            }
                          }`,
                          {input:this.settings});
    }
  }
}
</script>